/**
 * Trusted Types default policy (ZK Phase 9.3 — architecture §12, plan §9.3).
 *
 * `require-trusted-types-for 'script'` (nginx.conf / nginx-test.conf) makes the browser refuse
 * any plain-string assignment to a DOM XSS injection sink (innerHTML/outerHTML, eval, Worker
 * script URLs, ...) unless a Trusted Types policy coerces it first. The browser only
 * auto-applies a policy named "default" to sinks hit by code that isn't Trusted-Types-aware.
 *
 * Two such sinks exist in this app:
 *   - `EncryptionService` constructs the crypto Worker via
 *     `new Worker(new URL('./crypto.worker', import.meta.url))` — a TrustedScriptURL sink.
 *   - The vendored `flatpickr` date-picker library (`DatePickerDirective`, applied to every
 *     `input[appDatePicker]` across Orders/Work Orders/Visits/Sales/Spending/...) sets
 *     `.innerHTML` in three places: the prev/next month nav arrows (its default SVG chevron
 *     icons) and the weekday header labels — an HTML sink. This was missed in the initial
 *     Phase 9.3 sink audit (which only checked `FE/src/app`, not bundled third-party libraries)
 *     and surfaced as a real regression across every date-bearing form once Trusted Types
 *     enforcement was turned on: `require-trusted-types-for 'script'` silently rejected those
 *     `innerHTML` writes, so the calendar's arrow/weekday cells never rendered.
 *
 * `createHTML` therefore validates rather than blanket-accepts. The first attempt parsed the
 * input with `DOMParser` to walk it structurally — but in this browser, `DOMParser.
 * parseFromString()` itself routes through the same Trusted Types enforcement as the live
 * document, so calling it from inside our own `createHTML` recursed into `createHTML` again and
 * blew the call stack (`Maximum call stack size exceeded`, caught by ZK-151/ZK-152). Validation
 * is therefore pure string/regex matching — no DOM API touches the input at all: reject any
 * dangerous tag/attribute pattern outright, then require every tag name present to be on a small
 * allowlist. flatpickr's arrow/weekday markup passes; a realistic XSS payload reaching this sink
 * (`<img onerror=...>`, `<script>`, a `javascript:` URL) does not — a "default" policy that
 * accepted every string would defeat the point of Trusted Types.
 *
 * `createScript` is left unimplemented: nothing in this app legitimately needs an eval-like
 * sink, so any code path that reaches for one (ours or injected via XSS) gets a hard TypeError.
 */
// The TypeScript DOM lib does not ship Trusted Types definitions; declare the minimal shape
// this file actually uses (mirrors the codebase's existing `declare var lucide: any;` pattern
// for untyped browser globals in base-layout.component.ts / login.page.ts).
interface TrustedTypesPolicyFactoryLike {
  createPolicy(name: string, rules: {
    createHTML: (input: string) => string;
    createScriptURL: (input: string) => string;
  }): unknown;
}

// flatpickr's only innerHTML sinks: its default prev/next month SVG chevrons and the
// weekday-label <span> markup (see flatpickr/dist/flatpickr.js — monthNav/weekdayContainer).
const SAFE_STATIC_MARKUP_TAGS = new Set(['svg', 'path', 'g', 'circle', 'line', 'polyline', 'polygon', 'rect', 'ellipse', 'span']);

// Any of these anywhere in the string is an instant reject, regardless of tag allowlisting
// below — covers script/embedding tags, inline event-handler attributes, and javascript: URLs.
const DANGEROUS_MARKUP_PATTERN = /<\s*(script|iframe|object|embed|style|link|meta)\b|on[a-z]+\s*=|javascript\s*:/i;

// Matches every opening/closing tag name in the string, e.g. "<svg" / "</path" / "<br/>".
const TAG_NAME_PATTERN = /<\/?\s*([a-zA-Z][a-zA-Z0-9-]*)/g;

function isSafeStaticMarkup(html: string): boolean {
  if (html === '') return true;
  if (DANGEROUS_MARKUP_PATTERN.test(html)) return false;

  let match: RegExpExecArray | null;
  let sawAnyTag = false;
  TAG_NAME_PATTERN.lastIndex = 0;
  while ((match = TAG_NAME_PATTERN.exec(html)) !== null) {
    sawAnyTag = true;
    if (!SAFE_STATIC_MARKUP_TAGS.has(match[1].toLowerCase())) return false;
  }
  // A string with no tags at all (plain text) is not something this policy needs to allow —
  // flatpickr's sinks are always markup. Reject rather than silently pass through unknown shapes.
  return sawAnyTag;
}

export function installTrustedTypesPolicy(): void {
  const trustedTypes = (window as unknown as { trustedTypes?: TrustedTypesPolicyFactoryLike }).trustedTypes;
  if (!trustedTypes?.createPolicy) return; // unsupported browser — sinks stay plain strings

  trustedTypes.createPolicy('default', {
    createHTML: (input: string) => {
      if (!isSafeStaticMarkup(input)) {
        throw new Error('Trusted Types: rejected HTML that does not match an allowed static-markup shape');
      }
      return input;
    },
    createScriptURL: (input: string) => {
      const resolved = new URL(input, window.location.origin);
      if (resolved.origin !== window.location.origin) {
        throw new Error(`Trusted Types: rejected cross-origin script URL: ${input}`);
      }
      return input;
    },
  });
}
